import { useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { useElements, useStripe } from "@stripe/react-stripe-js"
import { getApiErrorMessage } from "helpers/embedded"
import { countDays } from "utils/dateSpan"
import { isDynamicMultiDay } from "utils/multiDayPricing"
import { capturePayment, createEmbeddededBooking, createPaymentIntent, fetchWidgetCart, hasCartError, updateEmbeddededBooking } from "store/experiences"
import { capturePayPalOrder, createPayPalOrder } from "store/payment-provider"
import { CART_ERRORS, CART_HOLD_STATUSES } from "../constants"
import { buildBookingPayload, CHECKOUT_PAYMENT_OPTIONS, EMBEDDED_BOOKING_PARAMS, getAvailabilityError } from "./payment"
import { createGuestCard } from "./cardPayment"

const PAID_INTENT_STATUSES = ["succeeded", "processing", "requires_capture"]
// Pay with the item's hold while the API holds (or held) its places; an expired hold still books
// while the places are free.
const HOLD_STATUSES_FOR_PAY = new Set([CART_HOLD_STATUSES.held, CART_HOLD_STATUSES.expired])
const CAPTURED_STATUSES = ["succeeded", "requested"]

// Books and pays the cart's single item. Same requests and the same double-charge guards as the
// old widget's checkout, on one page: the booking is created (or updated, on a retry) when the
// guest pays, then the card / wallet / PayPal flow runs.
//  - isSubmittingRef blocks a second Confirm while one is running, and stays locked once a card has
//    been charged so nothing can create a second charge.
//  - paymentIntentRef keeps the intent of the last attempt; a retry first asks Stripe whether that
//    intent was already paid (e.g. the connection dropped after charging) before confirming again.
//    It's dropped whenever the amount can change (payment option, promo codes, booking details).
// A ticketed create refused because places changed (getAvailabilityError) goes to onUnavailable
// instead of the error line, so the page can say what changed.
const useWidgetCheckout = ({ experience, selection, promos, onBooked, onUnavailable }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const stripe = useStripe()
  const elements = useElements()
  const [isPaying, setIsPaying] = useState(false)
  const [paymentError, setPaymentError] = useState(null)
  const isSubmittingRef = useRef(false)
  const paymentIntentRef = useRef(null)
  const bookingRef = useRef(null)
  // Set when the last booking attempt was refused for availability, so PayPal's own error is skipped.
  const isUnavailableRef = useRef(false)
  const isMultiDay = experience?.duration_type === "multi_day"
  const durationDays = isDynamicMultiDay(experience) ? countDays(selection?.date, selection?.endDate) : 0

  const resetPaymentIntent = () => {
    paymentIntentRef.current = null
  }

  // The error line for a failed attempt, unless it was refused for availability (onUnavailable).
  const showError = (error) => {
    const unavailable = getAvailabilityError(error)
    isUnavailableRef.current = !!unavailable
    if (unavailable && onUnavailable) {
      setPaymentError(null)
      onUnavailable(unavailable)
      return
    }
    setPaymentError(getApiErrorMessage(error) || t("booking_widget.checkout.errors.generic"))
  }

  // A new booking takes over the cart item's hold. If the API no longer has that hold, or it doesn't
  // match the item (e.g. checkout lowered the tickets and the hold couldn't follow), it's booked
  // without the hold, its places checked as for any booking.
  const createBooking = async (values) => {
    const holdId = HOLD_STATUSES_FOR_PAY.has(selection?.holdStatus) ? selection.holdId : null
    const payload = { experienceId: experience?.uuid, selection, values, isMultiDay, durationDays }
    if (!holdId) return dispatch(createEmbeddededBooking(buildBookingPayload(payload)))
    try {
      return await dispatch(createEmbeddededBooking(buildBookingPayload({ ...payload, holdId }), { isQuiet: true }))
    } catch (error) {
      if (!hasCartError(error, CART_ERRORS.holdNotFound) && !hasCartError(error, CART_ERRORS.holdMismatch)) throw error
      return dispatch(createEmbeddededBooking(buildBookingPayload(payload)))
    }
  }

  // Creates the booking with the guest's details and payment option, or updates the one an earlier
  // attempt created. Resolves with the booking ({ id, uuid, guest_token, client, ... }).
  const ensureBooking = async ({ values, paymentOption }) => {
    const existing = bookingRef.current
    const booking = existing?.uuid
      ? await dispatch(updateEmbeddededBooking(existing.uuid, buildBookingPayload({ experienceId: experience?.uuid, selection, values, isMultiDay, durationDays, isUpdate: true })))
      : await createBooking(values)
    if (!booking?.uuid) throw new Error("booking_not_created")
    // The new booking took over the item's hold: the cart reads it "booked" and its timer stops showing.
    if (!existing?.uuid) dispatch(fetchWidgetCart())
    const payment_option = CHECKOUT_PAYMENT_OPTIONS[paymentOption] || CHECKOUT_PAYMENT_OPTIONS.full
    if (payment_option !== CHECKOUT_PAYMENT_OPTIONS.full || existing?.uuid) {
      await dispatch(updateEmbeddededBooking(booking.uuid, { ...EMBEDDED_BOOKING_PARAMS, booking: { payment_option } }))
    }
    // Updates may answer with less than the create did (e.g. no guest_token); keep what we had.
    bookingRef.current = { ...existing, ...booking }
    resetPaymentIntent()
    return bookingRef.current
  }

  const ensurePaymentIntent = async (booking) => {
    if (paymentIntentRef.current?.clientSecret) return { ...paymentIntentRef.current, isReusingIntent: true }
    const response = await dispatch(createPaymentIntent(booking.id, booking.guest_token, promos.map((promo) => promo.code)))
    if (!response?.client_secret) return null
    paymentIntentRef.current = { clientSecret: response.client_secret, incomeId: response?.income?.id }
    return { ...paymentIntentRef.current, isReusingIntent: false }
  }

  // Runs only after the card was charged, so any failure here gets the "contact support" message
  // (never the generic "try again", which would invite a second charge).
  const finish = async (booking, incomeId) => {
    let capture = null
    try {
      capture = await dispatch(capturePayment(booking.id, incomeId))
    } catch {
      capture = null
    }
    if (!CAPTURED_STATUSES.includes(capture?.status)) {
      // Charged, but the booking wasn't confirmed: keep the guard locked so a retry can't charge again.
      setPaymentError(t("booking_widget.checkout.errors.capture"))
      return false
    }
    onBooked(booking)
    return true
  }

  const createCardPaymentMethod = async (booking, values) => {
    const { paymentMethodId, error } = await createGuestCard({ stripe, elements, dispatch, values, clientId: booking?.client?.id, guideId: experience?.guide?.id })
    if (!paymentMethodId) setPaymentError(error || t("booking_widget.checkout.errors.card"))
    return paymentMethodId || null
  }

  // Confirm and Pay with the card fields.
  const payByCard = async ({ values, paymentOption }) => {
    if (isSubmittingRef.current || !stripe || !elements) return
    isSubmittingRef.current = true
    setIsPaying(true)
    setPaymentError(null)
    let isCharged = false
    try {
      const booking = paymentIntentRef.current ? bookingRef.current : await ensureBooking({ values, paymentOption })
      const paymentMethodId = await createCardPaymentMethod(booking, values)
      if (!paymentMethodId) return
      const intent = await ensurePaymentIntent(booking)
      if (!intent) {
        setPaymentError(t("booking_widget.checkout.errors.generic"))
        return
      }
      let paymentIntent = null
      if (intent.isReusingIntent) {
        const { paymentIntent: existingIntent } = await stripe.retrievePaymentIntent(intent.clientSecret)
        if (PAID_INTENT_STATUSES.includes(existingIntent?.status)) paymentIntent = existingIntent
      }
      if (!paymentIntent) {
        const result = await stripe.confirmCardPayment(intent.clientSecret, { payment_method: paymentMethodId })
        if (result?.error) {
          setPaymentError(result.error.message)
          return
        }
        paymentIntent = result?.paymentIntent
      }
      if (!paymentIntent?.id) {
        setPaymentError(t("booking_widget.checkout.errors.generic"))
        return
      }
      isCharged = true
      await finish(booking, intent.incomeId)
    } catch (error) {
      showError(error)
    } finally {
      setIsPaying(false)
      if (!isCharged) isSubmittingRef.current = false
    }
  }

  // Apple / Google Pay: the wallet sheet hands over a payment method; book, then charge it.
  const payByWallet = async (event, { values, paymentOption }) => {
    if (isSubmittingRef.current) {
      event.complete("fail")
      return
    }
    isSubmittingRef.current = true
    setIsPaying(true)
    setPaymentError(null)
    let isCharged = false
    try {
      const booking = await ensureBooking({ values, paymentOption })
      const intent = await ensurePaymentIntent(booking)
      if (!intent) {
        event.complete("fail")
        setPaymentError(t("booking_widget.checkout.errors.generic"))
        return
      }
      const { paymentIntent, error } = await stripe.confirmCardPayment(intent.clientSecret, { payment_method: event.paymentMethod.id }, { handleActions: false })
      if (error) {
        event.complete("fail")
        setPaymentError(error.message)
        return
      }
      event.complete("success")
      let finalIntent = paymentIntent
      if (paymentIntent?.status === "requires_action") {
        const result = await stripe.confirmCardPayment(intent.clientSecret)
        if (result?.error) {
          setPaymentError(result.error.message)
          return
        }
        finalIntent = result?.paymentIntent
      }
      if (!finalIntent?.id) return
      isCharged = true
      await finish(booking, intent.incomeId)
    } catch (error) {
      event.complete("fail")
      showError(error)
    } finally {
      setIsPaying(false)
      if (!isCharged) isSubmittingRef.current = false
    }
  }

  // PayPal: the booking is created when the guest opens the PayPal window, then the order is
  // captured on approval — and the guest stays in the widget (the old widget left the embed here).
  const createPayPalOrderForBooking = async ({ values, paymentOption }) => {
    setPaymentError(null)
    isUnavailableRef.current = false
    let booking
    try {
      booking = await ensureBooking({ values, paymentOption })
    } catch (error) {
      if (getAvailabilityError(error)) showError(error)
      throw error
    }
    const payload = { trip_booking_id: booking.id, ...(promos.length && { promo_codes: promos.map((promo) => promo.code) }) }
    const response = await dispatch(createPayPalOrder(payload))
    return response?.order_id
  }

  // PayPal's onError, after a failed order or approval. A booking refused for availability already
  // says so.
  const handlePayPalError = () => {
    if (!isUnavailableRef.current) setPaymentError(t("booking_widget.checkout.errors.paypal"))
  }

  const approvePayPalOrder = async (orderId) => {
    const booking = bookingRef.current
    if (!orderId || !booking) return
    setIsPaying(true)
    try {
      const response = await dispatch(capturePayPalOrder({ order_id: orderId, trip_booking_id: booking.id }))
      if (response?.success) onBooked(booking)
      else setPaymentError(t("booking_widget.checkout.errors.paypal"))
    } catch {
      setPaymentError(t("booking_widget.checkout.errors.paypal"))
    } finally {
      setIsPaying(false)
    }
  }

  return {
    isPaying,
    paymentError,
    setPaymentError,
    resetPaymentIntent,
    payByCard,
    payByWallet,
    createPayPalOrderForBooking,
    approvePayPalOrder,
    handlePayPalError
  }
}

export default useWidgetCheckout
